import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { ButtonIcon, FormFeedback, TagInput } from '@morressier/alembic';
import { get } from 'lodash';
import { FieldError, UseFormReturn } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { useEffect, useRef } from 'react';
import { FormFieldTagInput } from '.';
import { ITagInputValue } from './TagInputUpdateModal';
import { toSentenceCase } from 'src/utils/string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';

interface Props {
  config: FormFieldTagInput;
  methods: UseFormReturn<ITagInputValue>;
  type?: 'keywords';
}

export const TagInputForm: React.FC<Props> = ({
  config,
  methods,
  type = 'keywords'
}) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    getValues
  } = methods;
  const tagInputWrapperRef = useRef<HTMLDivElement>(null);

  const handleAddKeyword = () => {
    const input = tagInputWrapperRef.current?.querySelector('input');
    const value = input?.value?.trim();
    if (input && value) {
      const current = getValues('temp') || [];
      setValue('temp', [...current, { text: value }]);
      input.value = '';
    }
  };
  const {
    params: {
      validations: {
        maxCountOfKeywords,
        required,
        minCountOfKeywords,
        maxLength
      },
      labels
    },
    caseDataFields: { main }
  } = config;

  // TODO: change the next 10 lines, don't use hook
  const values = watch('temp');
  const mainError = get(errors, main);

  const getArrayFieldErrorMessage = (fieldLabel?: string) => {
    if (!mainError) return undefined;

    if ((mainError as FieldError)?.message) {
      return (mainError as FieldError).message?.includes(
        'must NOT have fewer than'
      )
        ? `A minimum of ${minCountOfKeywords} ${toSentenceCase(
            fieldLabel || ''
          )} is required`
        : (mainError as FieldError).message;
    }

    if (Array.isArray(mainError)) {
      const firstError = mainError.find(Boolean);
      if (firstError?.message?.includes('must NOT have more than')) {
        return `Each ${toSentenceCase(
          fieldLabel || ''
        )} must be ${maxLength} characters or fewer`;
      }
      return firstError?.message;
    }

    return undefined;
  };

  const processedMainError = getArrayFieldErrorMessage(labels.main);

  useEffect(() => {
    setValue(
      main as any,
      values.map((value: { text: string }) => value.text)
    );
  }, [values]);

  return (
    <Box>
      <FieldLabel
        isRequired={required}
        label={`Update ${labels.main}`}
        subText={labels.subtitle}
        helpText={labels.helpText}
      />
      <Flex gap={2} alignItems="center" ref={tagInputWrapperRef}>
        <Box flex={1}>
          <TagInput
            marginBottom={0}
            isInvalid={Boolean(mainError)}
            control={control}
            name="temp"
            sx={{
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
              '& span': {
                whiteSpace: 'normal !important',
                wordBreak: 'break-word'
              }
            }}
          />
        </Box>
        <Box display={{ base: 'block', md: 'none' }}>
          <Tooltip
            hasArrow
            shouldWrapChildren
            placement="top"
            label="Add Keyword"
            borderRadius={4}
          >
            <ButtonIcon
              size="small"
              ariaLabel="keywords-add-button"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={handleAddKeyword}
            />
          </Tooltip>
        </Box>
      </Flex>
      <Flex justifyContent={'space-between'}>
        <Text marginTop={1} marginBottom={0}>
          Press Enter to add {type}
          {minCountOfKeywords !== undefined
            ? `. (Minimum ${minCountOfKeywords} required)`
            : ''}
        </Text>
        {maxCountOfKeywords && (
          <Text marginTop={1} marginBottom={0}>
            {values.length}/{maxCountOfKeywords}
          </Text>
        )}
      </Flex>
      {processedMainError && (
        <FormFeedback variant="error">{processedMainError}</FormFeedback>
      )}
    </Box>
  );
};
